import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Trash2, Pencil, Loader2, MessageCircle } from "lucide-react";

const empty = { name: "", email: "", password: "", phone: "" };

export default function SellersManager() {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/sellers").then(({ data }) => setSellers(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(empty); setError(""); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, email: s.email, password: "", phone: s.phone }); setError(""); setOpen(true); };

  const save = async () => {
    setError("");
    if (!form.name.trim() || form.name.trim().length < 2) { setError("El nombre debe tener al menos 2 caracteres"); return; }
    if (!editing && form.password.length < 6) { setError("La contraseña temporal debe tener al menos 6 caracteres"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/sellers/${editing.id}`, { name: form.name, phone: form.phone });
        toast.success("Vendedor actualizado");
      } else {
        await api.post("/sellers", form);
        toast.success("Vendedor creado");
      }
      setOpen(false);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const toggleAvail = async (s) => {
    try {
      const { data } = await api.patch(`/sellers/${s.id}/availability`, { available: !s.available });
      setSellers((prev) => prev.map((x) => (x.id === s.id ? { ...x, available: data.available } : x)));
    } catch (e) { toast.error("Error"); }
  };

  const toggleActive = async (s) => {
    try {
      const { data } = await api.put(`/sellers/${s.id}`, { active: !s.active });
      setSellers((prev) => prev.map((x) => (x.id === s.id ? { ...x, active: data.active } : x)));
    } catch (e) { toast.error("Error"); }
  };

  const del = async (s) => {
    if (!window.confirm(`¿Eliminar a ${s.name}? Sus leads se conservan pero quedan sin vendedor activo.`)) return;
    try { await api.delete(`/sellers/${s.id}`); toast.success("Vendedor eliminado"); load(); }
    catch (e) { toast.error("Error al eliminar"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-50">Vendedores</h1>
          <p className="text-slate-400 text-sm">{sellers.length} vendedores &middot; {sellers.filter(s => s.active && s.available).length} disponibles ahora</p>
        </div>
        <Button data-testid="add-seller-button" onClick={openNew} className="bg-[#25D366] hover:bg-[#22BF5B] text-[#0B0F14] font-semibold">
          <UserPlus className="w-4 h-4 mr-2" /> Nuevo vendedor
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-[#25D366] animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {sellers.map((s) => (
            <div key={s.id} data-testid={`seller-row-${s.id}`} className="bg-[#111822] border border-[#1E2D3D] rounded-xl p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-slate-100">{s.name}</h3>
                  <p className="text-xs text-slate-500">{s.email}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full border ${s.active ? "bg-[#0B2E1E] text-[#25D366] border-[#10B98140]" : "bg-[#2A181A] text-[#F87171] border-[#EF444440]"}`}>
                  {s.active ? "Activo" : "Baja"}
                </span>
              </div>
              <a href={`https://wa.me/${s.phone}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#25D366] font-mono-lf text-sm mb-4 hover:text-[#4ADE80]">
                <MessageCircle className="w-4 h-4" /> +{s.phone}
              </a>
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-slate-400">Disponible</span>
                <Switch data-testid={`seller-toggle-status-${s.id}`} checked={s.available} onCheckedChange={() => toggleAvail(s)} className="data-[state=checked]:bg-[#25D366]" />
              </div>
              <div className="flex items-center justify-between text-xs text-slate-500 border-t border-[#1E2D3D] pt-3">
                <span className="font-mono-lf">{s.lead_count} leads</span>
                <div className="flex gap-1">
                  <Button data-testid={`seller-edit-button-${s.id}`} onClick={() => openEdit(s)} size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-[#25D366]"><Pencil className="w-4 h-4" /></Button>
                  <Button data-testid={`seller-active-button-${s.id}`} onClick={() => toggleActive(s)} size="sm" variant="ghost" className="h-8 text-xs text-slate-400 hover:text-amber-400">{s.active ? "Dar de baja" : "Reactivar"}</Button>
                  <Button data-testid={`seller-delete-button-${s.id}`} onClick={() => del(s)} size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111822] border-[#1E2D3D] text-slate-100">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Editar vendedor" : "Nuevo vendedor"}</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">El vendedor usará estos datos para iniciar sesión y recibir leads por WhatsApp.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-slate-300 mb-1.5 block">Nombre</Label>
              <Input data-testid="seller-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0B0F14] border-[#1E2D3D]" /></div>
            <div><Label className="text-slate-300 mb-1.5 block">Correo {editing && <span className="text-xs text-slate-500">(no editable)</span>}</Label>
              <Input data-testid="seller-email-input" type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0B0F14] border-[#1E2D3D] disabled:opacity-50" /></div>
            {!editing && (
              <div><Label className="text-slate-300 mb-1.5 block">Contraseña temporal</Label>
                <Input data-testid="seller-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-[#0B0F14] border-[#1E2D3D]" /></div>
            )}
            <div><Label className="text-slate-300 mb-1.5 block">WhatsApp (con código de país)</Label>
              <Input data-testid="seller-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+593 99 123 4567" className="bg-[#0B0F14] border-[#1E2D3D] font-mono-lf" /></div>
            {error && <p data-testid="seller-form-error" className="text-sm text-red-400">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-slate-400">Cancelar</Button>
            <Button data-testid="seller-save-button" onClick={save} disabled={saving} className="bg-[#25D366] hover:bg-[#22BF5B] text-[#0B0F14] font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
